import { Alert, Button, Card, Divider, App, Radio, Row, Select, Space, Spin, Table } from 'antd';
import i18next from 'i18next';
import moment from 'moment';
import { RangeValue } from 'src/core/ui/date-components/utils';
import React, { Dispatch, FC, SetStateAction, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ENV } from 'src/';
import useMediaQuery from 'src/common/hooks/use-media-query';
import { emptyTableModel, getDateFormat } from 'src/common/utils';
import { formatMessage } from 'src/core/services/http.service';
import { Query } from 'src/core/stores/data-store';
import { TableModel, TableView } from 'src/core/ui/collections/table';
import { Languages } from 'src/i18n';
import { AppConfig, container } from 'src/inversify.config';
import {
  CsvFirstLineReportKeys,
  CsvSecondLineReportKeys,
  OrganizationStatisticsCsvReportRequest,
  OrganizationStatisticsDatatem,
  OrganizationStatisticsStore,
  OrganizationStatisticsSummaryItem,
  OrganizationStatisticsQuery,
  OrganizationStatisticsState,
} from 'src/stores/statistics-store';
import { AppConfiguration } from 'src/core/services/configuration.service';
import DatePicker from 'src/core/ui/date-components/date-picker';
import { ToolUsageReportModal } from '../tool-usage-report';
import { PermissionsConstants } from 'src/common/types';
import { useCurrentUserTenantPermissionsFilter } from 'src/common/permission-hooks';
import { useIdentity } from 'src/core/services/authentication';

const { RangePicker } = DatePicker;
const permissions = [PermissionsConstants.ManageAdminOrganization];

interface DateFilter {
  startDate: moment.Moment;
  endDate: moment.Moment;
}

const OrganizationStatisticsPage: FC<{
  queryState: OrganizationStatisticsState;
  setQueryState: Dispatch<SetStateAction<OrganizationStatisticsState>>;
}> = ({ queryState, setQueryState }) => {
  const { t } = useTranslation();
  const config: AppConfiguration = useContext(AppConfig);
  const onMobile = useMediaQuery('(max-width: 768px)');
  const identity = useIdentity();
  const [userPermissionsSet, checkingPermissions] = useCurrentUserTenantPermissionsFilter(identity, permissions);

  const queryParameters = useRef<{ startDate?: string; endDate?: string; mostUsed?: boolean }>(queryState.query);

  const [query, setQuery] = useState({
    searchQuery: '',
    skip: 0,
    take: 10,
    parameters: queryParameters.current,
  } as Query);

  const [isLoading, setIsLoading] = useState(true);
  const statisticsStore = container.get(OrganizationStatisticsStore);
  const statisticsState = statisticsStore.state;
  const statisticsSumary = useRef<OrganizationStatisticsSummaryItem>();
  const [selectFilterOpen, setSelectFilterOpen] = useState(false);
  const [selectedCustomDate, setSelectedCustomDate] = useState<any>();
  const [selectedValue, setSelectedValue] = useState<string | undefined>(queryState.selectorLabel);
  const labelRef = useRef(queryState.selectorLabel);
  const currentFilter = useRef<DateFilter>();
  const today = moment(moment.now());
  const [mostUsedOptionVisible, setMostUsedOptionVisible] = useState<boolean>(
    queryState.query.startDate !== undefined || queryState.query.endDate !== undefined
  );
  const currentCsvSeparator = useRef(',');
  const { modal } = App.useApp();

  const filters = ['Last24Hours', 'Last3Days', 'Last7Days', 'Last28Days'];
  type FilterOption = typeof filters[number];
  const [toolUsageReportModalVisible, setToolUsageReportModalVisible] = useState(false);

  useEffect(
    () => {
      load(query);
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const load = async (query: Query) => {
    setQueryState({ query: query.parameters as OrganizationStatisticsQuery, selectorLabel: labelRef.current });
    await statisticsStore.load(query);
    const statisticsSummaryItem = statisticsStore.getSummary();
    statisticsSumary.current = statisticsSummaryItem;
    setIsLoading(false);
  };

  const tableModel = {
    query,
    columns: [
      {
        title: t('Organization'),
        renderer: (data) => <span>{data.name}</span>,
      },
      {
        title: t('ProjectsNumber'),
        renderer: (data) => <span>{data.projects}</span>,
      },
      {
        title: t('UsersNumber'),
        renderer: (data) => <span>{data.users}</span>,
      },
      {
        title: t('SurveysNumber'),
        renderer: (data) => <span>{data.surveys}</span>,
      },
      {
        title: t('CreationDate'),
        renderer: (data) => <span>{moment.utc(data.createdDate).local().locale(i18next.language).format(`${getDateFormat()}, hh:mm a`)}</span>,
      },
      {
        title: t('LastUpdate'),
        renderer: (data) => <span>{moment.utc(data.updatedDate).local().locale(i18next.language).format(`${getDateFormat()}, hh:mm a`)}</span>,
      },
    ],
    data: { ...statisticsState.value, isBusy: statisticsState.value.isBusy },
  } as TableModel<OrganizationStatisticsDatatem>;

  const setCustomFilter = (filter: RangeValue<moment.Moment>) => {
    const customLabel = `${filter![0]!.format(getDateFormat())} - ${filter![1]!.format(getDateFormat())}`;
    labelRef.current = customLabel;

    setSelectedCustomDate(filter);

    if (filter !== null) {
      onFilterChange(filter[0], filter[1]);
    } else {
      onFilterChange(null, null);
    }

    setSelectedValue(customLabel);
  };

  const setPresetFilter = (key: FilterOption) => {
    setMostUsedOptionVisible(true);
    setSelectedCustomDate(undefined);
    switch (key) {
      case 'Last24Hours':
        currentFilter.current = { startDate: moment(today.clone().subtract(24, 'hour')), endDate: today };
        break;
      case 'Last3Days':
        currentFilter.current = { startDate: moment(today.clone().subtract(3, 'day')), endDate: today };
        break;
      case 'Last7Days':
        currentFilter.current = { startDate: moment(today.clone().subtract(7, 'day')), endDate: today };
        break;
      case 'Last28Days':
        currentFilter.current = { startDate: moment(today.clone().subtract(28, 'day')), endDate: today };
        break;
    }
    onSelectFilter();
  };

  const onFilterChange = (startDate: moment.Moment | null, endDate: moment.Moment | null) => {
    if (!startDate || !endDate) {
      currentFilter.current = undefined;
      setSelectedValue(undefined);
    } else {
      setMostUsedOptionVisible(true);
      startDate.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
      endDate.add(1, 'day');
      endDate.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
      endDate.subtract(1, 'minute');
      currentFilter.current = { startDate, endDate };
    }
    onSelectFilter();
    setSelectFilterOpen(false);
  };

  const clearSelection = () => {
    setMostUsedOptionVisible(false);
    queryParameters.current.mostUsed = false;
    onFilterChange(null, null);
    setSelectedCustomDate(undefined);
    setSelectFilterOpen(false);
    labelRef.current = undefined;
    setQueryState({
      query: queryParameters.current,
      selectorLabel: undefined,
    });
  };

  const onSelectFilter = () => {
    let newQuery;
    if (currentFilter.current) {
      queryParameters.current.startDate = currentFilter.current.startDate.toISOString();
      queryParameters.current.endDate = currentFilter.current.endDate.toISOString();

      newQuery = {
        ...query,
        filter: [`filterDate ge ${currentFilter.current.startDate.toISOString()} and filterDate le ${currentFilter.current.endDate.toISOString()}`],
        parameters: queryParameters.current,
      };
    } else {
      queryParameters.current.startDate = undefined;
      queryParameters.current.endDate = undefined;
      newQuery = {
        ...query,
        filter: undefined,
      };
    }
    setIsLoading(true);
    setQuery(newQuery);
    load(newQuery);
  };

  const onExportCsv = async () => {
    const item: OrganizationStatisticsCsvReportRequest = {
      csvSeparator: currentCsvSeparator.current,
      currentLanguage: i18next.language !== Languages.Russian ? i18next.language : 'ru-RU',
      startDate: currentFilter.current?.startDate.toISOString(),
      endDate: currentFilter.current?.endDate.toISOString(),
      headersFirstLine: Object.keys(CsvFirstLineReportKeys).map((v) => ({
        key: v,
        translatedHeader: t(v),
      })),
      headersSecondLine: Object.keys(CsvSecondLineReportKeys).map((v) => ({
        key: v,
        translatedHeader: t(v),
      })),
      mostUsed: queryParameters.current.mostUsed ?? false,
    };

    statisticsStore.exportCsv(item);
  };

  return (
    <Spin spinning={isLoading}>
      <Row align="top" justify="space-between">
        <Card className="card-table hide-table-header" style={{ width: '100%' }}>
          {statisticsSumary.current && !isLoading && (
            <>
              <Button
                style={{
                  margin: '20px',
                }}
                type="primary"
                onClick={() =>
                  modal.confirm({
                    title: (
                      <>
                        {t('Reports')} <Divider style={{ marginBottom: 2, marginTop: 16 }} />
                      </>
                    ),
                    onOk: onExportCsv,
                    okText: t('Export'),
                    cancelText: t('Cancel'),
                    icon: undefined,
                    width: 520,
                    content: (
                      <div style={{ margin: 20 }}>
                        {t('Separator')}:
                        <br />
                        <Select
                          defaultValue={currentCsvSeparator.current}
                          style={{ width: '100%' }}
                          onChange={(v) => (currentCsvSeparator.current = v)}
                          options={[
                            { label: t('Comma'), value: ',' },
                            { label: t('Semicolon'), value: ';' },
                            { label: t('Tab'), value: '\t' },
                          ]}
                        />
                      </div>
                    ),
                  })
                }
              >
                {t('ExportCSV')}
              </Button>
              {!checkingPermissions && userPermissionsSet.has(PermissionsConstants.ManageAdminOrganization) && (
                <Button type="primary" onClick={() => setToolUsageReportModalVisible(true)}>
                  {t('ToolUsageReport')}
                </Button>
              )}
              <Select
                value={selectedValue}
                allowClear
                onClear={clearSelection}
                style={{ width: 240, margin: onMobile ? '10px 15px 10px' : '20px 40px 20px', position: 'relative', float: 'right' }}
                placeholder={t('FilterByDate')}
                open={selectFilterOpen}
                onDropdownVisibleChange={(visible) => setSelectFilterOpen(visible)}
                onSelect={(selected) => {
                  labelRef.current = selected;
                  setSelectedValue(selected);
                  setPresetFilter(selected);
                }}
                dropdownRender={(menu) => (
                  <>
                    {menu}
                    <div
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                    >
                      <Divider style={{ margin: '8px 0' }} />
                      <Space style={{ padding: '0 8px 4px' }}>
                        <span>{t('CustomDateRange')}</span>
                      </Space>
                      <Space style={{ padding: '0 8px 4px' }}>
                        <RangePicker
                          classNames={{
                            popup: {
                              root: 'responsive-range-picker',
                            },
                          }}
                          value={selectedCustomDate}
                          onChange={setCustomFilter}
                          allowClear={false}
                        />
                      </Space>
                      <Button
                        type="primary"
                        style={{ width: 'calc(100% - 20px)', height: '40px', margin: '10px' }}
                        disabled={!selectedValue}
                        onClick={clearSelection}
                      >
                        {t('ClearFilter')}
                      </Button>
                    </div>
                  </>
                )}
                options={filters.map((value) => ({ label: t(value), value }))}
              />
              <div
                style={{
                  position: 'relative',
                  float: onMobile ? 'left' : 'right',
                  margin: onMobile ? '10px' : '25px 0 0',
                }}
                hidden={!mostUsedOptionVisible}
              >
                <span style={{ margin: 5 }}>{`${t('Show')}: `}</span>
                <Radio.Group
                  name="email-frequency"
                  style={{ marginTop: 5 }}
                  defaultValue={queryParameters.current.mostUsed}
                  onChange={(event) => {
                    queryParameters.current.mostUsed = event.target.value;
                    const newQuery = {
                      ...query,
                      parameters: queryParameters.current,
                    };
                    setQuery(newQuery);
                    load(newQuery);
                  }}
                >
                  <Radio value={true}>{t('Changed')}</Radio>
                  <Radio value={false}>{t('Unchanged')}</Radio>
                </Radio.Group>
              </div>
              <Divider />
              {statisticsState.errorMessage.value && (
                <Alert message={config.environment === ENV.DEV ? formatMessage(statisticsState.errorMessage.value) : t('UnexpectedError')} />
              )}
              <Table
                style={{ marginLeft: 40, marginRight: 40, marginBottom: 20 }}
                pagination={false}
                columns={[
                  { title: `${t('Organizations')}`, dataIndex: 'organizations' },
                  { title: `${t('Projects')}`, dataIndex: 'projects' },
                  { title: `${t('Surveys')}`, dataIndex: 'surveys' },
                  { title: `${t('Users')}`, dataIndex: 'users' },
                ]}
                dataSource={[statisticsSumary.current]}
              />
              <TableView
                hideFilter
                hideRefresh
                rowKey="id"
                canCreateNew={false}
                canDelete={false}
                canEdit={false}
                canSort
                onQueryChanged={(query: Query) => {
                  setQuery(query);
                  load(query);
                }}
                onRefresh={() => load(query)}
                model={statisticsState.errorMessage.value ? emptyTableModel : tableModel}
              />
            </>
          )}
        </Card>
      </Row>
      <ToolUsageReportModal onClose={() => setToolUsageReportModalVisible(false)} visible={toolUsageReportModalVisible} />
    </Spin>
  );
};

export default OrganizationStatisticsPage;
